import { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { valouseTranslations } from '../../translations/valouseTranslations';

const hameauPhotos = [
  { src: "/images/valouse-hameau/hameau-vue-aerienne-montagnes-valouse.jpg", name: "Hameau de Valouse", desc: "Vue aérienne du Hameau niché dans les gorges de la Drôme Provençale" },
  { src: "/images/valouse-hameau/hameau-chambre-poutres-confort-cocon.jpg", name: "Chambre du nouveau bâtiment", desc: "Confort moderne et atmosphère provençale" },
  { src: "/images/valouse-hameau/hameau-douche-italienne-lumineux.jpg", name: "Salle de bain lumineuse", desc: "Confort simple et élégant des chambres de Valouse" },
  { src: "/images/valouse-hameau/hameau-vue-aerienne-piscine-domaine.jpg", name: "Vue aérienne", desc: "Le Hameau de Valouse au cœur des paysages de la Drôme Provençale" },
  { src: "/images/valouse-hameau/hameau-cour-arbre-centenaire-cocon.jpg", name: "Cour du Hameau", desc: "Arbre centenaire et espace de repos" },
  { src: "/images/valouse-hameau/hameau-piscine-ressourcement-drome-provencale.jpg", name: "Piscine principale du Hameau", desc: "Un lieu de calme et de ressourcement" },
  { src: "/images/valouse-hameau/hameau-toits-provencaux-serenite-valouse.jpg", name: "Toits provençaux", desc: "Sérénité du Hameau de Valouse" },
  { src: "/images/valouse-hameau/hameau-lit-coussins-douceur-retraite.jpg", name: "Chambre du Hameau", desc: "Confort et douceur des lieux" },
  { src: "/images/valouse-hameau/hameau-terrasse-cypres-montagnes-serenite.jpg", name: "Terrasse panoramique", desc: "Vue sur les cyprès et les gorges de la Drôme" },
  { src: "/images/valouse-hameau/hameau-baignoire-pierre-voute-cocon.jpg", name: "Baignoire sous voûte", desc: "Charme ancien du Hameau de Valouse" },
  { src: "/images/valouse-hameau/hameau-porte-ouverte-chemin-eveil.jpg", name: "Jardin des cérémonies", desc: "Porte ouverte sur le jardin des cérémonies" },
  { src: "/images/valouse-hameau/hameau-chambre-pierre-bougies-intimite.jpg", name: "Chambre en pierre", desc: "Atmosphère authentique du Hameau" },
  { src: "/images/valouse-hameau/hameau-douche-italienne-bien-etre.jpg", name: "Douche italienne", desc: "Confort et simplicité provençale" },
  { src: "/images/valouse-hameau/hameau-chambre-provencale-porte-verte.jpg", name: "Chambre provençale", desc: "Lumière et calme du Hameau de Valouse" },
  { src: "/images/valouse-hameau/hameau-piscine-pierre-doree-cocon.jpg", name: "Seconde piscine et pierres dorées", desc: "Le Hameau dans sa lumière du sud" },
  { src: "/images/valouse-hameau/hameau-piscine-cypres-vue-balcon.jpg", name: "Piscine chauffée", desc: "Entre cyprès et collines de la Drôme Provençale" },
  { src: "/images/valouse-hameau/hameau-salle-bain-moderne-poutres.jpg", name: "Salle de bain contemporaine", desc: "Confort discret du Hameau de Valouse" },
  { src: "/images/valouse-hameau/hameau-cercle-bois-prairie-rituelle.jpg", name: "Espace du cercle", desc: "Lieu de pratiques et de rencontres COCON" },
];

const PlaceSection = () => {
  const { language } = useLanguage();
  const t = (obj) => obj[language] || obj['fr'];
  const tr = valouseTranslations;

  // Phase machine: 'idle' → 'text-out' → 'crossfade' → 'text-in' → 'idle'
  const [displayIdx, setDisplayIdx] = useState(0);
  const [nextIdx, setNextIdx] = useState(null);
  const [phase, setPhase] = useState('idle');
  const timerRef = useRef(null);
  const phaseRef = useRef(null);

  const clearTimers = useCallback(() => {
    clearInterval(timerRef.current);
    clearTimeout(phaseRef.current);
  }, []);

  const startTransition = useCallback((targetIdx) => {
    if (targetIdx === displayIdx || phase !== 'idle') return;
    clearTimers();

    // Phase 1: fade out text (quick)
    setPhase('text-out');
    phaseRef.current = setTimeout(() => {
      // Phase 2: crossfade images (smooth 1.5s like FragmentsPassage)
      setNextIdx(targetIdx);
      setPhase('crossfade');
      phaseRef.current = setTimeout(() => {
        // Phase 3: swap base image, fade in new text
        setDisplayIdx(targetIdx);
        setNextIdx(null);
        setPhase('text-in');
        phaseRef.current = setTimeout(() => {
          setPhase('idle');
        }, 300);
      }, 1500);
    }, 300);
  }, [displayIdx, phase, clearTimers]);

  // Auto-advance
  useEffect(() => {
    if (phase !== 'idle') return;
    timerRef.current = setInterval(() => {
      const next = (displayIdx + 1) % hameauPhotos.length;
      startTransition(next);
    }, 3500);
    return () => clearInterval(timerRef.current);
  }, [displayIdx, phase, startTransition]);

  const goTo = (idx) => {
    startTransition(idx);
  };

  const textVisible = phase === 'idle' || phase === 'text-in';
  const activeTextIdx = phase === 'crossfade' || phase === 'text-in' ? (nextIdx ?? displayIdx) : displayIdx;
  const photo = hameauPhotos[activeTextIdx];

  return (
    <section id="lieu" className="py-20 sm:py-28 bg-[#7e7472]">
      
      {/* Header */}
      <div className="max-w-2xl mx-auto text-center px-6 mb-16">
        <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#3F2A24] mb-6">
          Le lieu
        </p>
        <div className="w-12 h-[1px] bg-[#3F2A24]/30 mx-auto mb-8" />
        <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-[#F3E5CF] mb-8">
          Valouse
        </h2>
        <div className="space-y-2">
          <p className="text-sm sm:text-base text-[#F3E5CF]/70 leading-[2]">
            Un hameau de pierre du XVIe siècle.
          </p>
          <p className="text-sm sm:text-base text-[#F3E5CF]/70 leading-[2]">
            Un village entier, habituellement réservé aux mariages.
          </p>
        </div>
        <div className="space-y-2 pt-6">
          <p className="font-serif text-sm sm:text-base text-[#F3E5CF]/50 italic leading-[2]">
            Ce lieu ne se visite pas.
          </p>
          <p className="font-serif text-sm sm:text-base text-[#F3E5CF] italic leading-[2]">
            Il se traverse.
          </p>
        </div>
      </div>

      {/* ═══ ESPACE + VIDÉO + ESPACE ═══ */}
      <div className="h-12 sm:h-20" />
      
      <div className="max-w-4xl mx-auto px-6">
        <div className="aspect-[16/9] rounded-xl bg-[#56433e] overflow-hidden border border-[#E8D2B5]/15 relative group cursor-pointer"
          onClick={(e) => {
            const video = e.currentTarget.querySelector('video');
            if (video) {
              if (video.paused) { video.play(); e.currentTarget.querySelector('.play-overlay')?.classList.add('hidden'); }
              else { video.pause(); e.currentTarget.querySelector('.play-overlay')?.classList.remove('hidden'); }
            }
          }}
        >
          <video
            controls muted loop playsInline preload="auto"
            poster="/videos/valouse-village-poster.jpg"
            className="w-full h-full object-cover"
            data-testid="lieu-video-hameau"
          >
            <source src="/videos/valouse-village.mp4" type="video/mp4" />
            <source src="/videos/valouse-village.mov" type="video/quicktime" />
          </video>
          <div className="play-overlay absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
            <div className="w-16 h-16 rounded-full bg-[#F3E5CF]/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <svg className="w-6 h-6 text-[#3F2A24] ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
            </div>
          </div>
        </div>
      </div>

      <div className="h-12 sm:h-20" />

      {/* ═══ PREMIER BLOC POÉTIQUE ═══ */}
      <div className="max-w-lg mx-auto text-center px-6 space-y-8">
        <p className="font-serif text-lg sm:text-xl text-[#F3E5CF] leading-[2.4] italic">
          Privatisé dans son intégralité.
        </p>

        <div className="space-y-2">
          <p className="text-base sm:text-lg text-[#F3E5CF]/75 leading-[2]">
            Année après année, les dates se remplissent.
          </p>
          <p className="text-base sm:text-lg text-[#F3E5CF]/75 leading-[2]">
            Les célébrations s'y succèdent.
          </p>
        </div>

        <div className="h-2" />

        <p className="text-base sm:text-lg text-[#F3E5CF]/65 leading-[2]">
          Et puis, une fois l'été passé…
        </p>

        <p className="font-serif text-lg sm:text-xl text-[#F3E5CF] leading-[2.4]">
          Le lieu change de rythme.
        </p>
      </div>

      {/* Séparateur */}
      <div className="w-10 h-[1px] bg-[#C5A059]/40 mx-auto my-16 sm:my-20" />

      {/* ═══ SECOND BLOC POÉTIQUE ═══ */}
      <div className="max-w-lg mx-auto text-center px-6 space-y-8">
        <p className="font-serif text-lg sm:text-xl text-[#F3E5CF]/90 leading-[2.4]">
          Ce sera notre troisième édition.
        </p>

        <div className="space-y-2">
          <p className="text-base sm:text-lg text-[#F3E5CF] leading-[2]">
            Un nouveau COCON.
          </p>
          <p className="text-base sm:text-lg text-[#F3E5CF]/75 leading-[2]">
            En septembre.
          </p>
        </div>

        <div className="h-2" />

        <p className="text-base sm:text-lg text-[#F3E5CF]/65 leading-[2]">
          Après les unions, les fêtes, les promesses.
        </p>

        <p className="text-base sm:text-lg text-[#F3E5CF]/55 leading-[2]">
          Quand quelque chose est déjà chargé dans l'espace.
        </p>
      </div>

      {/* Séparateur */}
      <div className="w-10 h-[1px] bg-[#C5A059]/40 mx-auto my-16 sm:my-20" />

      {/* ═══ BLOC ÉNERGIE ═══ */}
      <div className="max-w-lg mx-auto text-center px-6 space-y-4">
        <p className="font-serif text-lg sm:text-xl text-[#F3E5CF] leading-[2.4]">
          Les énergies y sont différentes.
        </p>
        <div className="space-y-1">
          <p className="text-base sm:text-lg text-[#F3E5CF]/75 leading-[2]">Plus profondes.</p>
          <p className="text-base sm:text-lg text-[#F3E5CF]/75 leading-[2]">Plus ouvertes.</p>
          <p className="text-base sm:text-lg text-[#F3E5CF]/75 leading-[2]">Plus vivantes.</p>
        </div>
        <p className="text-base sm:text-lg text-[#F3E5CF] leading-[2] mt-4 italic">Et ça se sent immédiatement.</p>
      </div>

      {/* ═══ ESPACE + CARROUSEL + ESPACE ═══ */}
      <div className="h-20 sm:h-32" />

      <div className="max-w-4xl mx-auto px-6">
        <div data-testid="hameau-carousel">

          {/* Photo — crossfade fluide */}
          <div className="relative rounded-xl overflow-hidden shadow-2xl bg-[#56433e]">
            <img
              src={hameauPhotos[displayIdx].src}
              alt={`${hameauPhotos[displayIdx].name} – ${hameauPhotos[displayIdx].desc}`}
              className="w-full h-auto block"
              data-testid="hameau-carousel-img"
            />
            {nextIdx !== null && (
              <img
                src={hameauPhotos[nextIdx].src}
                alt={`${hameauPhotos[nextIdx].name} – ${hameauPhotos[nextIdx].desc}`}
                className="absolute inset-0 w-full h-auto block"
                style={{
                  opacity: phase === 'crossfade' ? 1 : 0,
                  transition: 'opacity 1.5s ease-in-out',
                }}
              />
            )}
          </div>

          {/* Texte — apparition synchronisée */}
          <div
            className="text-center mt-8 min-h-[3.5rem]"
            style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateY(0)' : 'translateY(6px)',
              transition: 'opacity 0.3s ease, transform 0.3s ease',
            }}
            data-testid="hameau-carousel-caption"
          >
            <p className="font-serif text-lg sm:text-xl text-[#F3E5CF] tracking-wide italic">
              {photo.name}
            </p>
            {photo.desc && (
              <p className="text-xs sm:text-sm text-[#F3E5CF]/40 tracking-[0.1em] mt-2">
                {photo.desc}
              </p>
            )}
          </div>

          {/* Indicateurs */}
          <div className="flex justify-center gap-2 mt-6">
            {hameauPhotos.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`rounded-full transition-all duration-1000 ${
                  displayIdx === i
                    ? "bg-[#C5A059] h-1.5 w-6"
                    : "bg-[#F3E5CF]/15 h-1.5 w-1.5 hover:bg-[#C5A059]/40"
                }`}
                aria-label={`Photo ${i + 1}`}
                data-testid={`hameau-dot-${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="h-20 sm:h-32" />

    </section>
  );
};

export default PlaceSection;
